// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { useRegion } from "../context/RegionContext";
import "./Product.css";
import { useParams } from "react-router-dom";
import { API_ENDPOINTS } from '../config/api';
import { robustFetch } from '../utils/apiUtils';
import LogoLoader from "../components/LogoLoader";
import Images from "./Images";
import { fetchHygraphProduct } from "./hygraphService";
// eslint-disable-next-line no-unused-vars
import paymentService from "./paymentService";
import buyNowService from "./buyNowService";
import { renderTiptapHTML } from "../Blog/tiptapRenderer";
import StoreProductReview from "./StoreProductReview";
import { getCartFromStorage, saveCartToStorage } from "./cartUtils";
import { generateProductStructuredData } from "../utils/structuredData";
import { getAuthUser } from "../utils/auth";



const IMAGE_URL_KEYS = ['url', 'src', 'displayImage', 'image', 'secure_url', 'secureUrl'];
const IMAGE_NESTED_KEYS = ['asset', 'node', 'file', 'fields', 'image', 'displayImage'];

const normalizeImageEntry = (image) => {
    if (!image) {
        return null;
    }

    if (typeof image === 'string') {
        const trimmed = image.trim();
        return trimmed ? { url: trimmed, altText: '' } : null;
    }

    if (Array.isArray(image)) {
        for (const entry of image) {
            const normalized = normalizeImageEntry(entry);
            if (normalized) {
                return normalized;
            }
        }
        return null;
    }

    if (typeof image !== 'object') {
        return null;
    }

    for (const key of IMAGE_URL_KEYS) {
        const candidate = image[key];
        if (typeof candidate === 'string' && candidate.trim()) {
            return {
                url: candidate.trim(),
                altText: image.altText || image.alt || image.title || image.name || ''
            };
        }
    }

    for (const key of IMAGE_NESTED_KEYS) {
        if (image[key]) {
            const nested = normalizeImageEntry(image[key]);
            if (nested) {
                return {
                    ...nested,
                    altText: nested.altText || image.altText || image.alt || image.title || image.name || ''
                };
            }
        }
    }

    return null;
};

const normalizeImageList = (images) => {
    if (!images) {
        return [];
    }

    const sourceList = Array.isArray(images) ? images : [images];
    return sourceList
        .map(normalizeImageEntry)
        .filter(Boolean);
};

const getFirstImageUrl = (images) => {
    const firstImage = normalizeImageList(images)[0];
    return firstImage ? firstImage.url : '';
};

// Helper function to find images in product data with multiple possible property names
const findProductImages = (product) => {
    // Check common image property names
    const possibleImageProps = ['images', 'image', 'productImages', 'photos', 'imageUrls', 'featuredImage', 'mainImage', 'thumbnail'];
    const collectedImages = [];
    
    for (const prop of possibleImageProps) {
        if (Array.isArray(product[prop]) && product[prop].length > 0) {
            collectedImages.push(...product[prop]);
        } else if (product[prop] && typeof product[prop] === 'object') {
            collectedImages.push(product[prop]);
        }
    }
    
    // If no array found, check for single image properties
    for (const prop of possibleImageProps) {
        if (product[prop] && typeof product[prop] === 'string' && product[prop].trim() !== '') {
            collectedImages.push(product[prop]);
        }
    }
    
    // Search for any property that might contain image data
    for (const [, value] of Object.entries(product)) {
        if (typeof value === 'string' && (value.includes('http') || value.includes('.jpg') || value.includes('.png') || value.includes('.webp'))) {
            collectedImages.push(value);
        }
    }
    
    return collectedImages;
};

const getProductGalleryImages = (product, hygraphData, isDigitalProduct) => {
    const seenUrls = new Set();

    const getUniqueImages = (imageSources) => imageSources
        .flatMap(source => normalizeImageList(source))
        .filter(image => {
            if (!image.url || seenUrls.has(image.url)) {
                return false;
            }

            seenUrls.add(image.url);
            return true;
        });

    const curatedDisplayImages = normalizeImageList(product.displayImage);
    if (curatedDisplayImages.length > 0) {
        return getUniqueImages([curatedDisplayImages]);
    }

    const fallbackSources = isDigitalProduct
        ? [hygraphData && hygraphData.productImages, product.images, findProductImages(product)]
        : [hygraphData && hygraphData.productImages, product.images, findProductImages(product)];

    return getUniqueImages(fallbackSources);
};

const addToCart = (product, quantity, hygraphData, selectedVariants = {}) => {
    const cart = getCartFromStorage();
    
    // Check for currency mismatch and clear cart if found
    if (cart.length > 0) {
        const existingCurrency = cart[0].currency || 'INR';
        const newCurrency = product.currency || 'INR';
        if (existingCurrency !== newCurrency) {
            // Currency mismatch - clear cart to avoid mixing currencies
            cart.length = 0;
            // Note: We might want to notify the user, but this function is synchronous.
            // The UI calling this will see the returned cart has only the new item.
        }
    }
    
    // Check if product is digital
    const isDigital = product.productType === 'digital';
    
    // For digital products: use productId only (no variants, always combine)
    // For physical products: include variants in uniqueId (separate entries for different variants)
    let uniqueId;
    if (isDigital) {
        // Digital products: always use productId only, ignore variants
        uniqueId = product._id;
    } else {
        // Physical products: include variants in uniqueId
        const variantString = Object.keys(selectedVariants).length > 0 
            ? Object.entries(selectedVariants).map(([key, value]) => `${key}:${value}`).sort().join('|')
            : '';
        uniqueId = variantString ? `${product._id}_${variantString}` : product._id;
    }
    
    // Find existing item
    // For digital products: match by productId only (always combine)
    // For physical products: match by uniqueId (productId + variants)
    const existingItemIndex = cart.findIndex(item => {
        if (isDigital) {
            // Digital products: match by productId only, ignore any variants or id format
            const itemProductId = item.productId || (item.id && item.id.split('_')[0]);
            return itemProductId === product._id;
        } else {
            // Physical products: match by exact uniqueId (includes variants)
            // Also handle case where item might have been added from grid (no variants)
            const itemHasVariants = item.variants && Object.keys(item.variants).length > 0;
            const currentHasVariants = Object.keys(selectedVariants).length > 0;
            
            // If both have no variants, match by productId
            if (!itemHasVariants && !currentHasVariants) {
                return item.productId === product._id || (item.id && item.id.split('_')[0] === product._id);
            }
            
            // Otherwise, match by exact uniqueId
            return item.id === uniqueId;
        }
    });
    
    if (existingItemIndex >= 0) {
        // Update quantity if product with same variants already exists
        cart[existingItemIndex].quantity += quantity;
        if (cart[existingItemIndex].quantity > 10) {
            cart[existingItemIndex].quantity = 10;
        }
    } else {
        // Add new product - select image using same priority as AllProducts page
        let imageUrl = '';
        if (isDigital) {
            const displayImageUrl = getFirstImageUrl(product.displayImage);
            const hygraphImageUrl = getFirstImageUrl(hygraphData && hygraphData.productImages);
            const mongoImageUrl = getFirstImageUrl(findProductImages(product));
            imageUrl = displayImageUrl || hygraphImageUrl || mongoImageUrl;
        } else {
            const hygraphImageUrl = getFirstImageUrl(hygraphData && hygraphData.productImages);
            const mongoImageUrl = getFirstImageUrl(findProductImages(product));
            const displayImageUrl = getFirstImageUrl(product.displayImage);
            imageUrl = hygraphImageUrl || mongoImageUrl || displayImageUrl;
        }
        
        cart.push({
            id: uniqueId,
            productId: product._id, // Keep original product ID for reference
            title: product.title,
            price: product.offerPrice || product.price,
            currency: product.currency,
            currencySymbol: product.currencySymbol,
            image: imageUrl,
            quantity: quantity,
            slug: product.slug,
            productType: product.productType || 'physical',
            variants: isDigital ? null : (Object.keys(selectedVariants).length > 0 ? selectedVariants : null)
        });
    }
    
    saveCartToStorage(cart);
    return cart;
};

function ProductContent() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [hygraphData, setHygraphData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hygraphLoading, setHygraphLoading] = useState(true);
    const [imagesLoading, setImagesLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isHardNotFound, setIsHardNotFound] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [cartMessage, setCartMessage] = useState('');
    const [selectedVariants, setSelectedVariants] = useState({});
    const [showRequiredFields, setShowRequiredFields] = useState(false);
    const [isInCart, setIsInCart] = useState(false);
    const { selectedCountry } = useRegion();

    // Logo Loader State
    const [showLoader, setShowLoader] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const imageDataRef = useRef({ images: [], displayImage: [] });


    useEffect(() => {
        if (!slug) return;

        imageDataRef.current = { images: [], displayImage: [] };
        setProduct(null);
        setHygraphData(null);
        setError(null);
        setIsHardNotFound(false);
        setImagesLoading(true);

        // Fetch product detail (no images — fast)
        const fetchProduct = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_ENDPOINTS.STORE.PRODUCT_BY_SLUG(slug)}?country=${selectedCountry || 'IN'}`);

                // Safe JSON parse — guards against Render.com cold-start returning "undefined"
                let data;
                try {
                    const rawText = await response.text();
                    const trimmed = rawText ? rawText.trim() : '';
                    if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
                        setError('Server returned an empty response. Please refresh the page.');
                        return;
                    }
                    data = JSON.parse(trimmed);
                } catch (parseErr) {
                    console.error('[Product] Failed to parse response:', parseErr);
                    setError('Failed to load product. Please refresh the page.');
                    return;
                }

                if (data && data.success && data.data) {
                    setProduct({
                        ...data.data,
                        images: imageDataRef.current.images.length > 0 ? imageDataRef.current.images : (data.data.images || []),
                        displayImage: imageDataRef.current.displayImage.length > 0 ? imageDataRef.current.displayImage : (data.data.displayImage || [])
                    });
                    // Fetch Hygraph data in parallel (non-blocking)
                    setHygraphLoading(true); // Start Hygraph loading
                    fetchHygraphProduct(slug)
                        .then(h => setHygraphData(h))
                        .catch(() => {})
                        .finally(() => setHygraphLoading(false)); // End Hygraph loading
                } else {
                    setIsHardNotFound(true);
                    setError('Product not found');
                }
            } catch (err) {
                setError('Failed to fetch product. Please refresh the page.');
                console.error('Error fetching product:', err);
            } finally {
                setLoading(false);
            }
        };

        // Fetch images lazily in parallel — merges into product state when ready
        const fetchImages = async () => {
            setImagesLoading(true);
            try {
                const response = await robustFetch(API_ENDPOINTS.STORE.PRODUCT_IMAGES(slug));
                const rawText = await response.text();
                const trimmed = rawText ? rawText.trim() : '';
                if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
                    setImagesLoading(false);
                    return;
                }
                let data;
                try { data = JSON.parse(trimmed); } catch (e) {
                    setImagesLoading(false);
                    return;
                }
                if (data && data.success && data.data) {
                    const nextImageData = {
                        images: data.data.images || [],
                        displayImage: data.data.displayImage || []
                    };
                    imageDataRef.current = nextImageData;
                    setProduct(prev => prev ? { ...prev, ...nextImageData } : prev);
                }
            } catch (err) {
                console.error('[Product] Failed to load images:', err);
            } finally {
                setImagesLoading(false);
            }
        };

        fetchProduct();
        fetchImages();
    }, [slug, selectedCountry]);

    // Initial loading effect
    useEffect(() => {
        let progress = 0;
        const interval = setInterval(() => {
            const isDataLoaded = !loading && !hygraphLoading;
            const maxProgress = isDataLoaded ? 100 : 90;
            
            if (progress < maxProgress) {
                const increment = Math.random() * 15;
                progress = Math.min(progress + increment, maxProgress);
                setLoadingProgress(progress);
            }

            if (isDataLoaded && progress >= 100) {
                clearInterval(interval);
                setTimeout(() => setShowLoader(false), 500);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [loading, hygraphLoading]);

    // Check if product is already in cart
    useEffect(() => {
        if (product) {
            const cart = getCartFromStorage();
            const found = cart.some(item => {
                const itemProductId = item.productId || (item.id && item.id.split('_')[0]);
                return itemProductId === product._id;
            });
            setIsInCart(found);
        }
    }, [product]);

    if (showLoader) {
        return <LogoLoader progress={loadingProgress} />;
    }
    
    if (error) {
        return (
            <>
                <Helmet>
                    <title>Product Not Found | Hari Vishnu Store</title>
                    <meta name="robots" content={isHardNotFound ? 'noindex,nofollow' : 'index,follow'} />
                </Helmet>
                <div className="error-message mx-auto my-[2%] flex min-h-[160px] w-[min(95%,900px)] items-center justify-center rounded-[12px] border border-[#fecaca] bg-[#fff5f5] px-[16px] py-[20px] text-center text-[#dc2626]">Error: {error}</div>
            </>
        );
    }

    if (!product) {
        return (
            <>
                <Helmet>
                    <title>Product Not Found | Hari Vishnu Store</title>
                    <meta name="robots" content="noindex,nofollow" />
                </Helmet>
                <div className="error-message mx-auto my-[2%] flex min-h-[160px] w-[min(95%,900px)] items-center justify-center rounded-[12px] border border-[#fecaca] bg-[#fff5f5] px-[16px] py-[20px] text-center text-[#dc2626]">Product not found</div>
            </>
        );
    }
    
    const displayPrice = product.offerPrice || product.price;
    const discountPercentage = product.price ? ((product.price - product.offerPrice) / product.price) * 100 : 0;
    const hasDiscount = product.offerPrice && product.offerPrice < product.price && Math.round(discountPercentage) >= 5;

    // Product data loaded successfully

    // Temporary fix: Add variants for iPhone if missing
    let productWithVariants = product;
    if (!product.variants && product.slug === 'iphone-16') {
        productWithVariants = {
            ...product,
            variants: [
                {
                    type: "Storage",
                    options: ["128 GB", "256 GB", "512 GB", "1 TB"],
                    tooltip: "Select your storage capacity"
                },
                {
                    type: "Color",
                    options: ["Blue", "Green", "Red", "Pink"],
                    tooltip: "Select your preferred color"
                }
            ]
        };
    }


    // Handle variant selection
    const handleVariantChange = (variantType, value) => {
        setSelectedVariants(prev => ({
            ...prev,
            [variantType]: value
        }));
        // Hide required field styling immediately when user selects a variant
        setShowRequiredFields(false);
    };





    // Check if all required variants are selected
    // For digital products, always return true (no variants needed)
    // For physical products, check if variants exist and are selected
    const isDigitalProduct = product.productType === 'digital';
    const allVariantsSelected = isDigitalProduct 
        ? true // Digital products don't need variants
        : (productWithVariants.variants && productWithVariants.variants.length > 0 
            ? (productWithVariants.variants[0].type || productWithVariants.variants[0].heading
                ? productWithVariants.variants.every(variant => selectedVariants[variant.type || variant.heading])
                : selectedVariants['size'] && selectedVariants['color'])
            : true); // If no variants exist, allow purchase

    // Helper function to check if HTML string has actual text content (not just empty tags)
    const hasTextContent = (htmlString) => {
        if (!htmlString || typeof htmlString !== 'string') return false;
        // Remove HTML tags and check if there's actual text content
        const textContent = htmlString.replace(/<[^>]*>/g, '').trim();
        return textContent.length > 0;
    };

    // Helper function to check if TipTap JSON has actual content
    const hasTipTapContent = (tiptapJson) => {
        if (!tiptapJson || typeof tiptapJson !== 'object') return false;
        
        // Check if it has content array
        if (tiptapJson.content && Array.isArray(tiptapJson.content)) {
            // Recursively check if any content node has text
            const checkNode = (node) => {
                if (node.text && node.text.trim() !== '') return true;
                if (node.content && Array.isArray(node.content)) {
                    return node.content.some(child => checkNode(child));
                }
                return false;
            };
            return tiptapJson.content.some(node => checkNode(node));
        }
        
        return false;
    };

    // Helper function to check if description has valid content
    const hasDescription = () => {
        // Check Hygraph description first
        if (hygraphData && hygraphData.productDescription) {
            const hygraphDesc = hygraphData.productDescription;
            if (hygraphDesc.html && hasTextContent(hygraphDesc.html)) return true;
            if (hygraphDesc.raw && hasTextContent(hygraphDesc.raw)) return true;
        }
        
        // Check product description
        if (product.description) {
            if (typeof product.description === 'string') {
                // String description - check if not empty and has text content
                return hasTextContent(product.description);
            } else if (typeof product.description === 'object') {
                // Object description - check various properties
                if (product.description.html && hasTextContent(product.description.html)) return true;
                if (product.description.raw && hasTextContent(product.description.raw)) return true;
                if (product.description.json) {
                    // TipTap JSON - check if it has actual content
                    if (hasTipTapContent(product.description.json)) return true;
                }
                // Direct TipTap JSON structure
                if (product.description.type === 'doc' && hasTipTapContent(product.description)) return true;
            }
        }
        
        return false;
    };

    // Build SEO image URL for structured data
    const productGalleryImages = getProductGalleryImages(product, hygraphData, isDigitalProduct);
    const seoImageUrl = productGalleryImages[0] ? productGalleryImages[0].url : '';

    const seoDescription = (() => {
        if (product.description && typeof product.description === 'string') {
            return product.description.replace(/<[^>]*>/g, '').trim().slice(0, 160);
        }
        return `Buy ${product.title} from Hari Vishnu Store. ${product.currencySymbol || '₹'}${displayPrice}.`;
    })();

    const productSchema = generateProductStructuredData({
        title: product.title,
        description: seoDescription,
        slug: product.slug,
        imageUrl: seoImageUrl || 'https://www.harivishnu.in/images/home_images/Logo.png',
        price: product.price,
        offerPrice: product.offerPrice,
        currency: product.currency,
        currencySymbol: product.currencySymbol,
        inStock: true,
    });

    const canonicalUrl = `https://store.harivishnu.in/${product.slug}`;

    const productBreadcrumbLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.harivishnu.in/" },
            { "@type": "ListItem", "position": 2, "name": "Store", "item": "https://store.harivishnu.in" },
            { "@type": "ListItem", "position": 3, "name": product.title, "item": canonicalUrl }
        ]
    };

    return (
        <>
        <Helmet>
            <title>{product.title} | Hari Vishnu Store</title>
            <meta name="description" content={seoDescription} />
            <meta name="robots" content="index,follow" />
            <link rel="canonical" href={canonicalUrl} />

            {/* Open Graph */}
            <meta property="og:type" content="product" />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={`${product.title} | Hari Vishnu Store`} />
            <meta property="og:description" content={seoDescription} />
            <meta property="og:image" content={seoImageUrl || 'https://www.harivishnu.in/images/home_images/Logo.png'} />
            <meta property="og:site_name" content="Hari Vishnu" />
            <meta property="product:price:amount" content={String(displayPrice)} />
            <meta property="product:price:currency" content={product.currency || 'INR'} />

            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={canonicalUrl} />
            <meta property="twitter:title" content={`${product.title} | Hari Vishnu Store`} />
            <meta property="twitter:description" content={seoDescription} />
            <meta property="twitter:image" content={seoImageUrl || 'https://www.harivishnu.in/images/home_images/Logo.png'} />

            {/* Structured Data (Product schema) */}
            <script type="application/ld+json">
                {JSON.stringify(productSchema)}
            </script>
            <script type="application/ld+json">
                {JSON.stringify(productBreadcrumbLd)}
            </script>
        </Helmet>
        <div className="product-area mx-auto my-[2rem] flex w-[min(95%,1400px)] flex-col overflow-x-hidden rounded-[15px] bg-bg px-[clamp(0.875rem,2vw,1.5rem)] py-[2rem] shadow-[0_4px_20px_rgba(0,0,0,0.1)] box-border">
            <div className="product-main-section grid w-full box-border grid-cols-[minmax(0,1.05fr)_minmax(320px,0.95fr)] items-start gap-[clamp(1.5rem,3vw,3rem)] max-[900px]:grid-cols-1">
                <div className="image-section min-w-0 w-full box-border">
                    {imagesLoading ? (
                        <div className="image-area w-full overflow-hidden rounded-[10px] bg-white">
                            <div className="no-image loading-image-placeholder flex h-[320px] items-center justify-center rounded-[8px] bg-[#f5f5f5] text-[#666]" style={{ height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: '8px' }}>
                                Loading images...
                            </div>
                        </div>
                    ) : (
                        <Images
                            images={productGalleryImages}
                        />
                    )}
                </div>

                <div className="product-details flex min-w-0 w-full flex-col gap-[1.25rem] box-border py-[clamp(0.25rem,1vw,0.75rem)]">
                <h1 className="pdt-name">{product.title}</h1>
                


                <div className="price-section mb-0 flex flex-wrap items-center gap-[15px]">
                    <p className="pdt-price m-0 inline-block text-[2em] font-[700] text-post-heading">{product.currencySymbol || '₹'}{displayPrice.toLocaleString()}</p>
                    {hasDiscount && (
                        <p className="original-price-container m-0 inline-block text-[1.2em] text-[#999]">
                            <span className="original-price line-through">{product.currencySymbol || '₹'}{product.price.toLocaleString()}</span>
                            <span className="discount-percentage m-0 text-[1.1em] font-[600] text-[#2ed573] no-underline"> ({Math.round(discountPercentage)}% OFF)</span>
                        </p>
                    )}
                </div>



                {product.tags && product.tags.length > 0 && (
                    <div className="tags-section mb-0">
                        <h3>Tags:</h3>
                        <div className="tags flex flex-wrap gap-[8px]">
                            {product.tags.map((tag, index) => (
                                <span key={index} className="tag rounded-[15px] bg-[#f1f2f6] px-[10px] py-[4px] text-[0.8em] text-[#666]">{tag}</span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Variant Selection Section - Only show for physical products with variants */}
                {(product.productType === 'physical' || !product.productType) && 
                 productWithVariants.variants && 
                 productWithVariants.variants.length > 0 && (
                    <div className="variants-section mb-0">
                        <h3>Select Options:</h3>
                        <div className="variants flex flex-col gap-[15px]">
                        {productWithVariants.variants && productWithVariants.variants.length > 0 && (productWithVariants.variants[0].type || productWithVariants.variants[0].heading) ? (
                            // New variant structure with type/heading, options/value, tooltip
                            productWithVariants.variants.map((variant, index) => {
                                const variantType = variant.type || variant.heading;
                                const variantOptions = variant.options || variant.value || [];
                                return (
                                <div key={index} className={`variant-option flex flex-col gap-[10px] ${showRequiredFields && !selectedVariants[variantType] ? 'required' : ''}`}>
                                    <label className="variant-label m-0 mb-[10px] text-[1.1em] font-[600] text-post-heading">{variantType}:</label>
                                    <div className="variant-options flex flex-wrap gap-[8px]">
                                        {variantOptions.map((option) => (
                                            <button
                                                key={option}
                                                type="button"
                                                className={`variant-btn rounded-[6px] border-2 border-[#e9ecef] bg-bg px-[16px] py-[8px] text-[0.9em] font-[500] text-text transition-all duration-300 hover:border-[#47b5ff] hover:bg-[#f8f9fa] ${selectedVariants[variantType] === option ? 'selected border-[#47b5ff] bg-[#47b5ff] text-white' : ''}`}
                                                onClick={() => handleVariantChange(variantType, option)}
                                                title={variant.tooltip || ''}
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )})
                        ) : productWithVariants.variants && productWithVariants.variants.length > 0 && productWithVariants.variants[0].size ? (
                            // Old variant structure with size, color, stock
                            <>
                                {/* Size Options */}
                                <div className={`variant-option flex flex-col gap-[10px] ${showRequiredFields && !selectedVariants['size'] ? 'required' : ''}`}>
                                    <label className="variant-label m-0 mb-[10px] text-[1.1em] font-[600] text-post-heading">Size:</label>
                                    <div className="variant-options flex flex-wrap gap-[8px]">
                                        {productWithVariants.variants.map((variant, index) => variant.size && (
                                            <button
                                                key={index}
                                                type="button"
                                                className={`variant-btn rounded-[6px] border-2 border-[#e9ecef] bg-bg px-[16px] py-[8px] text-[0.9em] font-[500] text-text transition-all duration-300 hover:border-[#47b5ff] hover:bg-[#f8f9fa] ${selectedVariants['size'] === variant.size ? 'selected border-[#47b5ff] bg-[#47b5ff] text-white' : ''}`}
                                                onClick={() => handleVariantChange('size', variant.size)}
                                            >
                                                {variant.size}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Color Options */}
                                <div className={`variant-option flex flex-col gap-[10px] ${showRequiredFields && !selectedVariants['color'] ? 'required' : ''}`}>
                                    <label className="variant-label m-0 mb-[10px] text-[1.1em] font-[600] text-post-heading">Color:</label>
                                    <div className="variant-options flex flex-wrap gap-[8px]">
                                        {productWithVariants.variants.map((variant, index) => variant.color && (
                                            <button
                                                key={index}
                                                type="button"
                                                className={`variant-btn rounded-[6px] border-2 border-[#e9ecef] bg-bg px-[16px] py-[8px] text-[0.9em] font-[500] text-text transition-all duration-300 hover:border-[#47b5ff] hover:bg-[#f8f9fa] ${selectedVariants['color'] === variant.color ? 'selected border-[#47b5ff] bg-[#47b5ff] text-white' : ''}`}
                                                onClick={() => handleVariantChange('color', variant.color)}
                                            >
                                                {variant.color}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        ) : (
                            // Fallback to default variants if none exist in database
                            <>
                                {/* Size Options */}
                                <div className={`variant-option flex flex-col gap-[10px] ${showRequiredFields && !selectedVariants['size'] ? 'required' : ''}`}>
                                    <label className="variant-label m-0 mb-[10px] text-[1.1em] font-[600] text-post-heading">Size:</label>
                                    <div className="variant-options flex flex-wrap gap-[8px]">
                                        {['S', 'M', 'L', 'XL', 'XXL'].map((size) => (
                                            <button
                                                key={size}
                                                type="button"
                                                className={`variant-btn rounded-[6px] border-2 border-[#e9ecef] bg-bg px-[16px] py-[8px] text-[0.9em] font-[500] text-text transition-all duration-300 hover:border-[#47b5ff] hover:bg-[#f8f9fa] ${selectedVariants['size'] === size ? 'selected border-[#47b5ff] bg-[#47b5ff] text-white' : ''}`}
                                                onClick={() => handleVariantChange('size', size)}
                                            >
                                                {size}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Color Options */}
                                <div className={`variant-option flex flex-col gap-[10px] ${showRequiredFields && !selectedVariants['color'] ? 'required' : ''}`}>
                                    <label className="variant-label m-0 mb-[10px] text-[1.1em] font-[600] text-post-heading">Color:</label>
                                    <div className="variant-options flex flex-wrap gap-[8px]">
                                        {['Blue', 'Green', 'Red'].map((color) => (
                                            <button
                                                key={color}
                                                type="button"
                                                className={`variant-btn rounded-[6px] border-2 border-[#e9ecef] bg-bg px-[16px] py-[8px] text-[0.9em] font-[500] text-text transition-all duration-300 hover:border-[#47b5ff] hover:bg-[#f8f9fa] ${selectedVariants['color'] === color ? 'selected border-[#47b5ff] bg-[#47b5ff] text-white' : ''}`}
                                                onClick={() => handleVariantChange('color', color)}
                                            >
                                                {color}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                        </div>
                    </div>
                )}



                {/* Product description - Only show if description exists and has content */}
                {hasDescription() && (
                    <div className="description-section mb-0">
                        <h3>Description:</h3>
                        {hygraphData && hygraphData.productDescription ? (
                            <div 
                                className="hygraph-description"
                                dangerouslySetInnerHTML={{ 
                                    __html: hygraphData.productDescription.html || hygraphData.productDescription.raw || '' 
                                }}
                            />
                        ) : product.description ? (
                            typeof product.description === 'string' ? (
                                <div 
                                    className="description"
                                    dangerouslySetInnerHTML={{ 
                                        __html: product.description 
                                    }}
                                />
                            ) : typeof product.description === 'object' ? (
                                // Handle TipTap JSON object
                                product.description.html ? (
                                    <div 
                                        className="description"
                                        dangerouslySetInnerHTML={{ 
                                            __html: product.description.html 
                                        }}
                                    />
                                ) : product.description.json ? (
                                    <div 
                                        className="description"
                                        dangerouslySetInnerHTML={{ 
                                            __html: renderTiptapHTML(product.description.json) 
                                        }}
                                    />
                                ) : product.description.raw ? (
                                    <div 
                                        className="description"
                                        dangerouslySetInnerHTML={{ 
                                            __html: product.description.raw 
                                        }}
                                    />
                                ) : product.description.type === 'doc' || (product.description.type && product.description.content) ? (
                                    // Direct TipTap JSON structure
                                    <div 
                                        className="description"
                                        dangerouslySetInnerHTML={{ 
                                            __html: renderTiptapHTML(product.description) 
                                        }}
                                    />
                                ) : null
                            ) : null
                        ) : null}
                    </div>
                )}

                {/* Quantity section - only show for physical products */}
                {!isDigitalProduct && (
                    <div className="quantity-section m-0">
                        <label htmlFor="quantity">Quantity:</label>
                        <div className="quantity-controls flex items-center gap-[10px] flex-wrap">
                            <button 
                                type="button" 
                                className="quantity-btn flex h-[40px] w-[40px] items-center justify-center rounded-[8px] border border-[#ddd] bg-white text-[1.1em] font-[700] text-[#333] transition-all duration-300 hover:bg-[#f2f7ff] disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                                disabled={quantity <= 1}
                            >
                                -
                            </button>
                            <input
                                type="number"
                                id="quantity"
                                min="1"
                                max="10"
                                value={quantity}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value) || 1;
                                    setQuantity(Math.max(1, Math.min(10, value)));
                                }}
                                className="quantity-input h-[40px] w-[90px] rounded-[8px] border border-[#ddd] px-[10px] text-center outline-none focus:border-[#47b5ff]"
                            />
                            <button 
                                type="button" 
                                className="quantity-btn flex h-[40px] w-[40px] items-center justify-center rounded-[8px] border border-[#ddd] bg-white text-[1.1em] font-[700] text-[#333] transition-all duration-300 hover:bg-[#f2f7ff] disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={() => setQuantity(prev => Math.min(10, prev + 1))}
                                disabled={quantity >= 10}
                            >
                                +
                            </button>
                            {isInCart && (
                                <button 
                                    type="button" 
                                    className="view-cart-btn rounded-[8px] border border-[#47b5ff] bg-white px-[14px] py-[8px] font-[600] text-[#47b5ff] transition-all duration-300 hover:bg-[#47b5ff] hover:text-white"
                                    onClick={() => window.location.href = '/cart'}
                                >
                                    View Cart
                                </button>
                            )}
                        </div>
                    </div>
                )}

                        {cartMessage && (
                            <div className={`cart-message rounded-[8px] px-[12px] py-[10px] text-[0.95rem] font-[600] ${cartMessage.toLowerCase().includes('added') ? 'success border border-[#bbf7d0] bg-[#f0fdf4] text-[#15803d]' : 'error border border-[#fecaca] bg-[#fff5f5] text-[#dc2626]'}`}>
                {cartMessage}
            </div>
        )}

                {/* View Cart button for digital products (physical products have it in the quantity section) */}
                {isDigitalProduct && isInCart && (
                    <button 
                        type="button" 
                        className="view-cart-btn rounded-[8px] border border-[#47b5ff] bg-white px-[14px] py-[8px] font-[600] text-[#47b5ff] transition-all duration-300 hover:bg-[#47b5ff] hover:text-white"
                        onClick={() => window.location.href = '/cart'}
                    >
                        View Cart
                    </button>
                )}

                <div className="action-buttons mt-[6px] flex flex-wrap items-center gap-[10px]">
                    {/* Hide Add to Cart for digital products already in cart */}
                    {!(isDigitalProduct && isInCart) && (
                    <button 
                        className="add-to-cart-btn rounded-[10px] border-0 bg-[#47b5ff] px-[16px] py-[11px] font-[700] text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-[#2ea3ff]"
                        onClick={() => {
                            if (!isDigitalProduct && !allVariantsSelected) {
                                // Show required field styling
                                setShowRequiredFields(true);
                                
                                const missingOptions = [];
                                if (productWithVariants.variants && productWithVariants.variants.length > 0) {
                                    if (productWithVariants.variants[0].type || productWithVariants.variants[0].heading) {
                                        // New variant structure
                                        productWithVariants.variants.forEach(variant => {
                                            const variantType = variant.type || variant.heading;
                                            if (!selectedVariants[variantType]) {
                                                missingOptions.push(variantType);
                                            }
                                        });
                                    } else {
                                        // Old variant structure
                                        if (!selectedVariants['size']) missingOptions.push('size');
                                        if (!selectedVariants['color']) missingOptions.push('color');
                                    }
                                } else {
                                    if (!selectedVariants['size']) missingOptions.push('size');
                                    if (!selectedVariants['color']) missingOptions.push('color');
                                }
                                setCartMessage(`Please select ${missingOptions.join(' and ')} before adding to cart`);
                                setTimeout(() => setCartMessage(''), 3000);
                                
                                // Remove required styling after 5 seconds
                                setTimeout(() => setShowRequiredFields(false), 5000);
                                return;
                            }
                            const updatedCart = addToCart(product, quantity, hygraphData, selectedVariants); // eslint-disable-line no-unused-vars
                            setIsInCart(true);
                            setCartMessage(`Added ${quantity} item(s) to cart!`);
                            setTimeout(() => setCartMessage(''), 3000);
                        }}
                    >
                        {isDigitalProduct || allVariantsSelected ? 'Add to Cart' : 'Select Options'}
                    </button>
                    )}
                    <button 
                        className="buy-now-btn rounded-[10px] border border-[#111] bg-[#111] px-[16px] py-[11px] font-[700] text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-[#2c2c2c]" 
                        onClick={async () => {
                            if (!isDigitalProduct && !allVariantsSelected) {
                                // Show required field styling
                                setShowRequiredFields(true);
                                
                                const missingOptions = [];
                                if (productWithVariants.variants && productWithVariants.variants.length > 0) {
                                    if (productWithVariants.variants[0].type || productWithVariants.variants[0].heading) {
                                        // New variant structure
                                        productWithVariants.variants.forEach(variant => {
                                            const variantType = variant.type || variant.heading;
                                            if (!selectedVariants[variantType]) {
                                                missingOptions.push(variantType);
                                            }
                                        });
                                    } else {
                                        // Old variant structure
                                        if (!selectedVariants['size']) missingOptions.push('size');
                                        if (!selectedVariants['color']) missingOptions.push('color');
                                    }
                                } else {
                                    if (!selectedVariants['size']) missingOptions.push('size');
                                    if (!selectedVariants['color']) missingOptions.push('color');
                                }
                                setCartMessage(`Please select ${missingOptions.join(' and ')} before buying`);
                                setTimeout(() => setCartMessage(''), 3000);
                                
                                // Remove required styling after 5 seconds
                                setTimeout(() => setShowRequiredFields(false), 5000);
                                return;
                            }
                            
                            try {
                                // Check if user is logged in
                                const userData = getAuthUser();
                                
                                if (userData) {
                                    // User is logged in, proceed with direct payment
                                    await buyNowService.buyNow(product, quantity, selectedVariants, userData);
                                } else {
                                    // Guest user, redirect to checkout
                                    await buyNowService.guestBuyNow(product, quantity, selectedVariants);
                                }
                            } catch (error) {
                                console.error('Error in buy now:', error);
                                setCartMessage('Failed to initiate purchase. Please try again.');
                                setTimeout(() => setCartMessage(''), 3000);
                            }
                        }}
                    >
                        Buy Now
                    </button>
                </div>
                </div>
            </div>

            {/* Product Reviews Section */}
            {product._id && (
                <div className="product-reviews-section mt-[20px] w-full">
                    <StoreProductReview productId={product._id} />
                </div>
            )}
        </div>
        </>
    );
}

export default function Product() {
    return <ProductContent />;
}
